
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw } from 'lucide-react';
import { AdminChart, adminChartConfig } from './AdminChartConfig';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

type UsageStats = {
  id: string;
  date: string;
  users: number;
  logins: number;
  credits_used: number;
  created_at: string;
  updated_at: string;
};

const StatsTab = () => {
  const [stats, setStats] = useState<UsageStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchStats();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('usage_stats_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'usage_stats' 
        }, 
        () => {
          fetchStats();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('usage_stats')
        .select('*')
        .order('date', { ascending: true })
        .limit(30);

      if (error) {
        throw error;
      }

      setStats(data || []);
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Statistiken.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const processedData = stats.map(stat => ({
    date: formatDate(stat.date),
    users: stat.users,
    logins: stat.logins,
    credits_used: stat.credits_used
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nutzungsstatistiken</CardTitle>
        <CardDescription>
          Übersicht über die Nutzung der Plattform in den letzten 30 Tagen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-jade" />
            <p>Statistiken werden geladen...</p>
          </div>
        ) : stats.length === 0 ? (
          <Alert>
            <AlertDescription>
              Keine Statistikdaten vorhanden.
            </AlertDescription>
          </Alert>
        ) : (
          <AdminChart data={processedData} height={400}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={processedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<ChartTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="users" 
                  stroke={adminChartConfig.users.color} 
                  name={adminChartConfig.users.label} 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="logins" 
                  stroke={adminChartConfig.logins.color} 
                  name={adminChartConfig.logins.label}
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="credits_used" 
                  stroke={adminChartConfig.credits_used.color} 
                  name={adminChartConfig.credits_used.label}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </AdminChart>
        )}
      </CardContent>
    </Card>
  );
};

// Custom tooltip component for the chart
const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border/50 rounded-lg p-3 shadow-lg">
        <div className="text-sm font-medium mb-2">{label}</div>
        {payload.map((item: any) => (
          <div key={item.name} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <div 
                className="h-2 w-2 rounded-full" 
                style={{ backgroundColor: item.color }}
              />
              <span>{item.name}</span>
            </div>
            <span className="font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default StatsTab;
